import { useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Trash2, Play, Pause, RotateCw } from "lucide-react";
import {
  Table,
  Select,
  Modal,
  Form,
  Input,
  DatePicker,
  InputNumber,
  Switch,
  Button,
  message,
  Popconfirm,
  Spin,
  Empty,
  Tag,
  Tooltip,
  Alert,
} from "antd";
import dayjs from "dayjs";
import Card from "../../../components/ui/Card";
import ButtonWrapper from "../../components/reuseComp/ButtonWrapper";
import { useTerm } from "../../../lib/terminology";
import { useClientList } from "../../../hooks/api/clients/hooks";
import { useJobTypeList } from "../../../hooks/api/job-config/hooks";
import { useFieldWorkerList } from "../../../hooks/api/field-workers/hooks";
import { useStripeRecurringPaymentStatus } from "../../../hooks/api/integrations/stripe-recurring-hooks";
import {
  useRecurringSchedulesList,
  useCreateRecurringSchedule,
  useUpdateRecurringSchedule,
  useDeleteRecurringSchedule,
  useToggleRecurringScheduleActive,
  useRunRecurringScheduleNow,
} from "../../../hooks/api/recurringSchedules/hooks";
import { IRecurringSchedule, IRecurringScheduleCreatePayload, TRecurringFrequency } from "../../../hooks/api/recurringSchedules/types";

// ── Frequency helpers ─────────────────────────────────────────────────────────

const FREQUENCY_OPTS: { value: TRecurringFrequency; label: string }[] = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "fortnightly", label: "Fortnightly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "biannually", label: "Biannually" },
  { value: "annually", label: "Annually" },
];

const FREQUENCY_LABEL: Record<TRecurringFrequency, string> = {
  daily: "Daily",
  weekly: "Weekly",
  fortnightly: "Fortnightly",
  monthly: "Monthly",
  quarterly: "Quarterly",
  biannually: "Biannually",
  annually: "Annually",
  custom: "Custom",
};

const INTERVAL_UNIT: Record<TRecurringFrequency, string> = {
  daily: "day(s)",
  weekly: "week(s)",
  fortnightly: "fortnight(s)",
  monthly: "month(s)",
  quarterly: "quarter(s)",
  biannually: "half-year(s)",
  annually: "year(s)",
  custom: "day(s)",
};

const DAY_OF_WEEK_OPTS = [
  { value: 0, label: "Sunday" },
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
];

/** "Every 2 weeks", "Weekly", "Every 3 months", etc — human-readable frequency + interval. */
function formatFrequency(frequency: TRecurringFrequency, intervalValue?: number | null): string {
  const n = intervalValue && intervalValue > 1 ? intervalValue : 1;
  const label = FREQUENCY_LABEL[frequency] ?? frequency;
  if (n <= 1) return label;
  const unit = INTERVAL_UNIT[frequency]?.replace("(s)", "s") ?? "times";
  return `Every ${n} ${unit}`;
}

function showsDayOfWeek(frequency?: TRecurringFrequency) {
  return frequency === "weekly" || frequency === "fortnightly";
}

function showsDayOfMonth(frequency?: TRecurringFrequency) {
  return frequency === "monthly" || frequency === "quarterly" || frequency === "biannually" || frequency === "annually";
}

// ── Create / Edit form ────────────────────────────────────────────────────────

function ScheduleFormModal({
  open,
  onClose,
  editing,
  clientOptions,
  jobTypeOptions,
  workerOptions,
  onSubmit,
  submitting,
}: {
  open: boolean;
  onClose: () => void;
  editing: IRecurringSchedule | null;
  clientOptions: { value: number; label: string }[];
  jobTypeOptions: { value: number; label: string }[];
  workerOptions: { value: number; label: string }[];
  onSubmit: (values: any) => void;
  submitting: boolean;
}) {
  const [form] = Form.useForm();
  const frequency: TRecurringFrequency | undefined = Form.useWatch("frequency", form);
  const autoCharge: boolean | undefined = Form.useWatch("auto_charge", form);
  const watchedClientId: number | undefined = Form.useWatch("client_id", form);
  const watchedJobTypeId: number | undefined = Form.useWatch("job_type_id", form);

  const { data: mandateRes } = useStripeRecurringPaymentStatus(watchedClientId ?? null);
  const mandateActive = mandateRes?.status === "active";

  const clientName = clientOptions.find((c) => c.value === watchedClientId)?.label ?? "";
  const jobTypeName = jobTypeOptions.find((j) => j.value === watchedJobTypeId)?.label ?? "";
  const titlePlaceholder =
    jobTypeName || clientName ? `${jobTypeName || "Service"} - ${clientName || "Client"}` : "e.g. Weekly Office Clean";

  useEffect(() => {
    if (!open) return;
    if (editing) {
      form.setFieldsValue({
        client_id: editing.client_id,
        job_type_id: editing.job_type_id ?? undefined,
        title: editing.title ?? undefined,
        frequency: editing.frequency,
        interval_value: editing.interval_value ?? 1,
        day_of_week: editing.day_of_week ?? undefined,
        day_of_month: editing.day_of_month ?? undefined,
        start_date: editing.start_date ? dayjs(editing.start_date) : undefined,
        end_date: editing.end_date ? dayjs(editing.end_date) : undefined,
        duration_mins: editing.duration_mins ?? undefined,
        assigned_user_id: editing.assigned_user_id ?? undefined,
        price: editing.price ?? undefined,
        auto_invoice: editing.auto_invoice ?? true,
        auto_charge: editing.auto_charge ?? false,
        notes: editing.notes ?? undefined,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ interval_value: 1, auto_invoice: true, auto_charge: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing]);

  function handleFinish(values: any) {
    const payload: IRecurringScheduleCreatePayload = {
      ...values,
      start_date: values.start_date ? values.start_date.format("YYYY-MM-DD") : undefined,
      end_date: values.end_date ? values.end_date.format("YYYY-MM-DD") : null,
    };
    if (!showsDayOfWeek(values.frequency)) delete (payload as any).day_of_week;
    if (!showsDayOfMonth(values.frequency)) delete (payload as any).day_of_month;
    onSubmit(payload);
  }

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={<span className="text-base font-semibold">{editing ? "Edit Recurring Schedule" : "New Recurring Schedule"}</span>}
      width="min(620px, 95vw)"
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" loading={submitting} onClick={() => form.submit()}>
          {editing ? "Save Changes" : "Create Schedule"}
        </Button>,
      ]}
      destroyOnClose
    >
      <Form form={form} layout="vertical" size="middle" className="pt-2" onFinish={handleFinish}>
        <div className="grid grid-cols-2 gap-x-4">
          <Form.Item name="client_id" label="Client" rules={[{ required: true, message: "Client is required" }]}>
            <Select
              showSearch
              allowClear
              placeholder="Select client…"
              options={clientOptions}
              filterOption={(input, opt) => ((opt?.label as string) ?? "").toLowerCase().includes(input.toLowerCase())}
            />
          </Form.Item>
          <Form.Item name="job_type_id" label="Job Type">
            <Select
              showSearch
              allowClear
              placeholder="Select job type…"
              options={jobTypeOptions}
              filterOption={(input, opt) => ((opt?.label as string) ?? "").toLowerCase().includes(input.toLowerCase())}
            />
          </Form.Item>
        </div>

        <Form.Item name="title" label="Title">
          <Input placeholder={titlePlaceholder} />
        </Form.Item>

        <div className="grid grid-cols-2 gap-x-4">
          <Form.Item name="frequency" label="Frequency" rules={[{ required: true, message: "Frequency is required" }]} initialValue="weekly">
            <Select options={FREQUENCY_OPTS} />
          </Form.Item>
          <Form.Item
            name="interval_value"
            label={`Repeat every N ${INTERVAL_UNIT[frequency ?? "weekly"]}`}
            initialValue={1}
            rules={[
              {
                validator: (_, value) => {
                  if (value === undefined || value === null || value === "") return Promise.resolve();
                  return Number.isInteger(Number(value)) && Number(value) >= 1
                    ? Promise.resolve()
                    : Promise.reject(new Error("Must be a whole number, 1 or greater"));
                },
              },
            ]}
          >
            <InputNumber className="w-full" min={1} step={1} precision={0} />
          </Form.Item>
        </div>

        {(showsDayOfWeek(frequency) || showsDayOfMonth(frequency)) && (
          <div className="grid grid-cols-2 gap-x-4">
            {showsDayOfWeek(frequency) && (
              <Form.Item name="day_of_week" label="Day of Week">
                <Select options={DAY_OF_WEEK_OPTS} placeholder="Select day…" allowClear />
              </Form.Item>
            )}
            {showsDayOfMonth(frequency) && (
              <Form.Item name="day_of_month" label="Day of Month">
                <InputNumber className="w-full" min={1} max={31} placeholder="1-31" />
              </Form.Item>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 gap-x-4">
          <Form.Item name="start_date" label="Start Date" rules={[{ required: true, message: "Start date is required" }]}>
            <DatePicker className="w-full" format="DD/MM/YYYY" />
          </Form.Item>
          <Form.Item name="end_date" label="End Date" help="Leave blank for no end date">
            <DatePicker className="w-full" format="DD/MM/YYYY" />
          </Form.Item>
        </div>

        <div className="grid grid-cols-2 gap-x-4">
          <Form.Item name="duration_mins" label="Duration (minutes)">
            <InputNumber className="w-full" min={0} placeholder="e.g. 60" />
          </Form.Item>
          <Form.Item name="assigned_user_id" label="Assigned Worker">
            <Select
              showSearch
              allowClear
              placeholder="Assign worker (optional)…"
              options={workerOptions}
              filterOption={(input, opt) => ((opt?.label as string) ?? "").toLowerCase().includes(input.toLowerCase())}
            />
          </Form.Item>
        </div>

        <Form.Item name="price" label="Price (£)">
          <InputNumber className="w-full" min={0} placeholder="0.00" />
        </Form.Item>

        <Form.Item
          name="auto_invoice"
          label="Auto-invoice"
          valuePropName="checked"
          initialValue={true}
          extra="Automatically create a draft invoice each time a job is generated"
        >
          <Switch />
        </Form.Item>

        <Form.Item
          name="auto_charge"
          label="Auto-charge"
          valuePropName="checked"
          initialValue={false}
          extra="Automatically collect payment via the client's saved card — requires the client to have set up recurring payment first"
        >
          <Switch />
        </Form.Item>

        {autoCharge && watchedClientId && !mandateActive && (
          <Alert
            type="warning"
            showIcon
            className="mb-4"
            message="No active recurring payment mandate"
            description="This client hasn't set up recurring payment yet (or it's still pending). Set it up from the client's detail view before relying on auto-charge."
          />
        )}

        <Form.Item name="notes" label="Notes">
          <Input.TextArea rows={2} placeholder="Internal notes…" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

const RecurringSchedulesPage = () => {
  const term = useTerm();
  const [clientFilter, setClientFilter] = useState<number | undefined>(undefined);
  const [activeFilter, setActiveFilter] = useState<string>(""); // "" | "active" | "paused"
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<IRecurringSchedule | null>(null);

  const queryData = useMemo(() => {
    const q: any = { page, limit: 20 };
    if (clientFilter) q.client_id = clientFilter;
    if (activeFilter === "active") q.is_active = true;
    if (activeFilter === "paused") q.is_active = false;
    return q;
  }, [page, clientFilter, activeFilter]);

  const { data: listRes, isLoading, isError, error, refetchWithQuery } = useRecurringSchedulesList(queryData);
  const schedules: IRecurringSchedule[] = (listRes?.data as IRecurringSchedule[] | undefined) ?? [];
  const total = Number(listRes?.extra?.total ?? 0);

  const { data: clientRes } = useClientList({ page: 1, limit: 200 });
  const clientOptions = ((clientRes?.data as any[]) ?? []).map((c) => {
    const name = c.business_name || `${c.first_name ?? ""} ${c.last_name ?? ""}`.trim() || "—";
    const sub = c.email || c.phone || c.mobile || "";
    return { value: c.id, label: sub ? `${name} (${sub})` : name };
  });

  const { data: jobTypeRes } = useJobTypeList({ page: 1, limit: 200 });
  const jobTypeOptions = ((jobTypeRes?.data as any[]) ?? []).map((jt) => ({ value: jt.id, label: jt.name }));
  const jobTypeNameById = useMemo(() => {
    const map = new Map<number, string>();
    jobTypeOptions.forEach((o) => map.set(o.value, o.label));
    return map;
  }, [jobTypeOptions]);

  const { data: workerRes } = useFieldWorkerList({ page: 1, limit: 200, status: "ACTIVE" });
  const workerOptions = ((workerRes?.data as any[]) ?? []).map((w: any) => {
    const name = `${w.first_name ?? ""} ${w.last_name ?? ""}`.trim() || w.email || "—";
    return { value: w.id, label: w.employee_number ? `${name} (${w.employee_number})` : name };
  });

  const { mutate: createSchedule, isPending: creating } = useCreateRecurringSchedule({
    qData: queryData,
    onSuccess: () => {
      message.success("Recurring schedule created.");
      closeModal();
    },
    onError: (err: any) => message.error(err?.response?.data?.message ?? err?.message ?? "Failed to create schedule."),
  });

  const { mutate: updateSchedule, isPending: updating } = useUpdateRecurringSchedule({
    onSuccess: () => {
      message.success("Recurring schedule updated.");
      closeModal();
    },
    onError: (err: any) => message.error(err?.response?.data?.message ?? err?.message ?? "Failed to update schedule."),
  });

  const { mutate: deleteSchedule } = useDeleteRecurringSchedule({
    onSuccess: () => message.success("Recurring schedule deleted."),
    onError: (err: any) => message.error(err?.response?.data?.message ?? err?.message ?? "Failed to delete schedule."),
  });

  const { mutate: toggleActive, isPending: toggling } = useToggleRecurringScheduleActive({
    onSuccess: (res) => message.success(res?.message || "Schedule updated."),
    onError: (err: any) => message.error(err?.response?.data?.message ?? err?.message ?? "Failed to update schedule."),
  });

  const { mutate: runNow, isPending: runningNow } = useRunRecurringScheduleNow({
    onSuccess: (res) => message.success(res?.message || "Job generated for today."),
    onError: (err: any) => message.error(err?.response?.data?.message ?? err?.message ?? "Failed to run schedule now."),
  });

  function openCreate() {
    setEditing(null);
    setModalOpen(true);
  }

  function openEdit(schedule: IRecurringSchedule) {
    setEditing(schedule);
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
    setEditing(null);
  }

  function handleSubmit(values: any) {
    if (editing) {
      updateSchedule({ id: editing.id, ...values });
    } else {
      createSchedule(values);
    }
  }

  const hasFilters = !!clientFilter || !!activeFilter;

  const columns = [
    {
      title: term("client").toUpperCase(),
      key: "client",
      render: (_: any, record: IRecurringSchedule) => (
        <div className="text-sm font-medium text-slate-700">{record.client_name ?? "—"}</div>
      ),
    },
    {
      title: "SERVICE",
      key: "service",
      render: (_: any, record: IRecurringSchedule) => (
        <span className="text-sm text-slate-600">
          {record.job_type_id ? jobTypeNameById.get(record.job_type_id) ?? `#${record.job_type_id}` : "—"}
        </span>
      ),
    },
    {
      title: "FREQUENCY",
      key: "frequency",
      render: (_: any, record: IRecurringSchedule) => (
        <span className="text-sm text-slate-600">{formatFrequency(record.frequency, record.interval_value)}</span>
      ),
    },
    {
      title: "NEXT RUN",
      dataIndex: "next_run_date",
      render: (v: string | null) => (v ? dayjs(v).format("DD MMM YYYY") : <span className="text-slate-400">—</span>),
    },
    {
      title: "PRICE",
      dataIndex: "price",
      render: (v: number | null) => (v != null ? `£${Number(v).toFixed(2)}` : <span className="text-slate-400">—</span>),
    },
    {
      title: "AUTO-INVOICE",
      dataIndex: "auto_invoice",
      render: (v: boolean) =>
        v ? <Tag color="success">Yes</Tag> : <Tag color="default">No</Tag>,
    },
    {
      title: "STATUS",
      dataIndex: "is_active",
      render: (v: boolean) =>
        v ? (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700">
            Active
          </span>
        ) : (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-500">
            Paused
          </span>
        ),
    },
    {
      title: "",
      key: "actions",
      render: (_: any, record: IRecurringSchedule) => (
        <div className="flex items-center gap-1 justify-end">
          <Tooltip title="Edit">
            <Button size="small" icon={<Pencil size={14} />} onClick={() => openEdit(record)} className="text-slate-500 hover:text-blue-600" />
          </Tooltip>
          <Popconfirm
            title="Generate today's job for this schedule now?"
            onConfirm={() => runNow(record.id)}
            okText="Run now"
            cancelText="Cancel"
          >
            <Tooltip title="Run now">
              <Button size="small" icon={<RotateCw size={14} />} loading={runningNow} className="text-slate-500 hover:text-primary" />
            </Tooltip>
          </Popconfirm>
          <Tooltip title={record.is_active ? "Pause" : "Resume"}>
            <Button
              size="small"
              icon={record.is_active ? <Pause size={14} /> : <Play size={14} />}
              loading={toggling}
              onClick={() => toggleActive(record.id)}
              className="text-slate-500 hover:text-amber-600"
            />
          </Tooltip>
          <Popconfirm
            title="Delete this recurring schedule?"
            description="This action cannot be undone."
            onConfirm={() => deleteSchedule({ ids: String(record.id) })}
            okText="Delete"
            okType="danger"
            cancelText="Cancel"
          >
            <Tooltip title="Delete">
              <Button size="small" icon={<Trash2 size={14} />} className="text-slate-500 hover:text-red-600" />
            </Tooltip>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div className="min-h-screen">
      {/* HEADER */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-slate-800">Recurring Schedules</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Automate recurring {term("job", true).toLowerCase()} and recurring payment collection for repeat {term("client", true).toLowerCase()}
          </p>
        </div>
        <ButtonWrapper type="primary" icon={<Plus size={16} />} onClick={openCreate} className="flex items-center gap-2">
          New Recurring Schedule
        </ButtonWrapper>
      </div>

      {/* FILTERS */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <Select
          showSearch
          allowClear
          placeholder={`Filter by ${term("client").toLowerCase()}…`}
          value={clientFilter}
          onChange={(v) => {
            setClientFilter(v);
            setPage(1);
          }}
          options={clientOptions}
          filterOption={(input, opt) => ((opt?.label as string) ?? "").toLowerCase().includes(input.toLowerCase())}
          className="w-64"
        />
        <Select
          value={activeFilter || undefined}
          onChange={(v) => {
            setActiveFilter(v ?? "");
            setPage(1);
          }}
          placeholder="All statuses"
          allowClear
          className="w-40"
          options={[
            { value: "active", label: "Active" },
            { value: "paused", label: "Paused" },
          ]}
        />
      </div>

      {/* ERROR */}
      {isError && (
        <div className="mb-4">
          <Alert
            type="error"
            showIcon
            message="Failed to load data"
            description={(error as any)?.message ?? "Please refresh the page or contact support."}
          />
        </div>
      )}

      {/* CONTENT */}
      {isLoading ? (
        <div className="flex justify-center items-center py-24">
          <Spin size="large" />
        </div>
      ) : schedules.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-100 py-16">
          <Empty
            description={
              <span className="text-slate-500">
                {hasFilters ? "No recurring schedules match your filters." : "No recurring schedules yet."}
              </span>
            }
          >
            {!hasFilters && (
              <Button type="primary" onClick={openCreate}>
                Create your first recurring schedule
              </Button>
            )}
          </Empty>
        </div>
      ) : (
        <Card className="overflow-hidden">
          <Table
            rowKey="id"
            dataSource={schedules}
            columns={columns as any}
            pagination={{
              current: page,
              pageSize: 20,
              total,
              showSizeChanger: false,
              showTotal: (t) => `${t} schedules`,
              onChange: (p) => {
                setPage(p);
                refetchWithQuery({ ...queryData, page: p });
              },
            }}
          />
        </Card>
      )}

      <ScheduleFormModal
        open={modalOpen}
        onClose={closeModal}
        editing={editing}
        clientOptions={clientOptions}
        jobTypeOptions={jobTypeOptions}
        workerOptions={workerOptions}
        onSubmit={handleSubmit}
        submitting={creating || updating}
      />
    </div>
  );
};

export default RecurringSchedulesPage;
